/**
 * Returns an absolute URL for an API that is reverse-proxied on the app.
 * @param capabilityName short name of the capability (i.e. `sso` or `tenancy`)
 * @param apiPath path of the API URL. Do *not* include a leading slash.
 * @param apiVersion version of the API to use (defaults to `1`)
 */
export function apiUrl(capabilityName: string, apiPath: string, apiVersion = 1) {
  return `/${capabilityName}/api/v${apiVersion}/${apiPath}`;
}

const apiRegionShortNames: Record<string, string> = {
  'us-east-1': 'us',
  'eu-central-1': 'eu',
  'ca-central-1': 'ca',
  'us-gov-west-1': 'us-gov',
  'ap-southeast-1': 'sg',
  'ap-southeast-2': 'au',
  'ap-northeast-2': 'kr',
  'af-south-1': 'sa',
  'me-central-1': 'me',
};

const getApiRegionShortName = (capabilityName: string, region: string): string => {
  if (capabilityName === 'data' && region === 'ap-southeast-2') {
    return 'ap';
  }
  return apiRegionShortNames[region];
};

/**
 * Returns a full URL for a regionally-deployed service.
 * @param capabilityName capability identifier as used in the domain (e.g. `lct`)
 * @param region AWS region (e.g. `us-east-1`)
 * @param stage deployment stage (e.g. `prod`, `dev`, `local`)
 * @param apiPath path of the API URL. Do *not* include a leading slash.
 * @param apiVersion version of the API to use (defaults to `1`)
 */
export function regionalApiUrl(props: {
  capabilityName: string;
  region: string;
  apiPath: string;
  apiVersion?: number;
  stage?: string;
}) {
  const { capabilityName, region, stage = props.stage || 'local', apiPath, apiVersion = 1 } = props;

  const shortName = getApiRegionShortName(capabilityName, region);
  const suffix = `/api/v${apiVersion}/${apiPath}`;

  if (stage === 'local') return `/${capabilityName}-${shortName}${suffix}`;
  if (stage !== 'prod')
    return `https://${shortName}.${stage}.${capabilityName}.api.bb-fnds.com/${capabilityName}${suffix}`;

  return `https://${shortName}.${capabilityName}.api.blackboard.com/${capabilityName}${suffix}`;
}
